import { useEffect, useRef, useState } from "react";

interface Props {
  /** Timestamp del último refetch exitoso (ms). Si null, no se ha sincronizado. */
  ultimaSync: number | string | null;
  /** True mientras el query está re-fetching. */
  fetching: boolean;
  /**
   * True si la última consulta al CRM falló. Fuerza el estado "sin conexión"
   * aunque haya un `ultimaSync` previo (react-query conserva la data anterior
   * en error). Sin esto, el header mentía "activa" cuando el CRM respondía error.
   */
  error?: boolean;
}

function tiempoRelativo(ts: number | null): string {
  if (!ts) return "sin sincronizar";
  const diff = Date.now() - ts;
  const seg = Math.floor(diff / 1000);
  if (seg < 60) return `hace ${seg}s`;
  const min = Math.floor(seg / 60);
  if (min < 60) return `hace ${min} min`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `hace ${hrs}h`;
  return `hace ${Math.floor(hrs / 24)}d`;
}

/**
 * Card "Conexión activa con Mercurio CRM" (UI-1).
 *
 * Visible solo para administrador. Métricas client-side:
 * - Endpoint: hardcode del host del CRM (no se expone secret).
 * - Última sync: timestamp del último refetch exitoso del worker.
 * - Latencia: tiempo entre que el fetch arranca y termina.
 * - Estado: activo / inactivo según el query.
 */
export function CardConexionMercurio({ ultimaSync, fetching, error = false }: Props) {
  const [tick, setTick] = useState(0);
  const [latencia, setLatencia] = useState<number | null>(null);
  const inicioFetchRef = useRef<number | null>(null);

  // Actualiza el "hace X min" cada 30s.
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);

  // Mide latencia del fetch: marca inicio cuando arranca; al terminar
  // calcula delta. Cuando `fetching` pasa true → false guarda el delta.
  useEffect(() => {
    if (fetching) {
      inicioFetchRef.current = performance.now();
    } else if (inicioFetchRef.current != null) {
      const delta = performance.now() - inicioFetchRef.current;
      setLatencia(Math.round(delta));
      inicioFetchRef.current = null;
    }
  }, [fetching]);

  const syncMs =
    typeof ultimaSync === "string" ? Date.parse(ultimaSync) : ultimaSync;
  // Activo = hubo una sync exitosa Y la última consulta no falló. El flag
  // `error` es la autoridad: refleja la conexión REAL al CRM, no una métrica
  // local (worker) que responde aunque Mercurio esté caído.
  const activo = !error && syncMs != null;
  // tick fuerza re-render para refrescar el "hace X min" sin recibir nueva data.
  void tick;
  return (
    <div
      className="card"
      style={{
        marginBottom: 18,
        background: "var(--surface)",
        borderColor: activo
          ? "oklch(0.83 0.10 162)"
          : "oklch(0.85 0.07 25)",
      }}
    >
      <div
        style={{
          padding: "14px 18px",
          display: "flex",
          alignItems: "center",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            minWidth: 280,
            flex: 1,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 999,
              background: activo
                ? "var(--emerald, oklch(0.55 0.16 162))"
                : "var(--red, oklch(0.55 0.18 25))",
              boxShadow: activo
                ? "0 0 0 4px oklch(0.83 0.10 162 / 0.3)"
                : "0 0 0 4px oklch(0.85 0.07 25 / 0.3)",
              flexShrink: 0,
            }}
          />
          <div>
            <div style={{ fontWeight: 600, color: "var(--ink)" }}>
              {activo
                ? "Conexión activa con Mercurio CRM"
                : "Sin conexión con Mercurio CRM"}
            </div>
            <div
              style={{
                fontSize: 12,
                color: "var(--ink-3)",
                marginTop: 2,
                lineHeight: 1.4,
              }}
            >
              Sólo se muestran PQRs en estado ABIERTO con paso "gestión
              backoffice". Las cerradas y las que ya están en GEMA quedan ocultas.
            </div>
          </div>
        </div>

        <Pill label="Última sync" valor={tiempoRelativo(syncMs)} />
        <Pill label="Endpoint" valor="mercurio.afinia.co" mono />
        <Pill
          label="Latencia"
          valor={latencia != null ? `${latencia} ms` : "—"}
          mono
        />
      </div>
    </div>
  );
}

function Pill({
  label,
  valor,
  mono = false,
}: {
  label: string;
  valor: string;
  mono?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minWidth: 110,
      }}
    >
      <span
        style={{
          fontSize: 11,
          color: "var(--ink-3)",
          textTransform: "uppercase",
          letterSpacing: 0.4,
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      <span
        className={mono ? "mono" : undefined}
        style={{
          fontSize: 13,
          color: "var(--ink)",
          fontWeight: 500,
        }}
      >
        {valor}
      </span>
    </div>
  );
}
